/**
 * Check if today is within a date range
 *
 * @param  start - Start date
 * @param  end - End date
 * @param  today - Date to check if between start and end
 *
 * @return - Is today is wihin start and end?
 */
export function isTodayInRange(start: Date, end: Date, today?: Date) {
  const startMs = start.getTime()
  const endMs = end.getTime()
  const todayMs = today?.getTime() ?? Date.now()
  return todayMs >= startMs && todayMs <= endMs
}

/**
 * Parse short date string into month day
 *
 * @param date - month / year ex: 12/1
 *
 * @return Return month and date, month is 0 based 0 -11 see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/getMonth
 *
 */
export function parseShortDate(date: string) {
  const dateSplit = date.split('/')
  return { month: Number(dateSplit[0]) - 1, date: Number(dateSplit[1]) }
}

/**
 * Check if today is within a recurring (annually) short date range
 *
 * @param start - Short start date ex: 12/1
 * @param end - Short end date
 * @param today - Date to check if between start and end
 *
 * @return Is today is wihin start and end?
 */
export function isTodayInRecurringShortDateRange(
  start: string,
  end: string,
  today: Date
) {
  const thisYear = new Date().getFullYear()
  const lastYear = thisYear - 1
  const nextYear = thisYear + 1
  const startMD = parseShortDate(start)
  const endMD = parseShortDate(end)
  // Does range overlap years, check both begining and end of this year
  if (startMD.month > endMD.month) {
    return (
      isTodayInRange(
        new Date(lastYear, startMD.month, startMD.date),
        new Date(thisYear, endMD.month, endMD.date),
        today
      ) ||
      isTodayInRange(
        new Date(thisYear, startMD.month, startMD.date),
        new Date(nextYear, endMD.month, endMD.date),
        today
      )
    )
  }
  return isTodayInRange(
    new Date(thisYear, startMD.month, startMD.date),
    new Date(thisYear, endMD.month, endMD.date),
    today
  )
}

/**
 * Get time difference between two dates
 *
 * @param  start - Start date
 * @param  end - End date
 * @returns  the amount of time between the two dates
 * If a date was 3 days, 7 hours, 23 minutes and 5 seconds ago it will return
 * {day: 3, hours: 7, minutes: 23, seconds: 5}
 */
export function getDifferenceBetweenDates(
  start: Date | string | number,
  end: Date | string | number
) {
  const diff = new Date(end).getTime() - new Date(start).getTime()
  const days = Math.floor(diff / 86400000)
  const hours = Math.floor((diff % 86400000) / 3600000)
  const minutes = Math.floor((diff % 3600000) / 60000)
  const seconds = Math.floor((diff % 60000) / 1000)

  return { days, hours, minutes, seconds }
}

/**
 * Get total time difference between two dates in multiple units
 *
 * @param  start - Start date
 * @param  end - End date
 * @returns the amount of time between the two dates
 * If a date was 3 days, 7 hours, 23 minutes and 5 seconds ago it will return
 * {day: 3, hours: 79, minutes: 4763, seconds: 285785}
 */
export function getTotalDifferenceBetweenDates(
  start: Date | string | number,
  end: Date | string | number
) {
  const { days, hours, minutes, seconds } = getDifferenceBetweenDates(
    start,
    end
  )

  const totalHours = days * 24 + hours
  const totalMinutes = totalHours * 60 + minutes
  const totalSeconds = totalMinutes * 60 + seconds

  return {
    weeks: Math.floor(days / 7),
    days,
    hours: totalHours,
    minutes: totalMinutes,
    seconds: totalSeconds,
  }
}

/**
 * Calculate today's date value, i.e. 1-365(366 for leap year) to compare against the
 * open dates. Formula taken from https://stackoverflow.com/questions/8619879/javascript-calculate-the-day-of-the-year-1-366
 * @returns the day of the year as a number
 */
export const getDayOfYear = (date?: string | Date) => {
  const now = date ? new Date(date) : new Date()
  const start = new Date(now.getFullYear(), 0, 0)
  const diff =
    now.getTime() -
    start.getTime() +
    (start.getTimezoneOffset() - now.getTimezoneOffset()) * 60 * 1000
  const oneDay = 1000 * 60 * 60 * 24
  const day = Math.floor(diff / oneDay)

  return day
}

/**
 * Get week number of the year. Ex: Dec 22nd = 51
 * Option to pass in date and determine week from that date. No arg uses current date
 * @param date - Javascript Date object
 * @returns Week of year
 */
export const getWeekOfYear = (date?: string | Date) => {
  const currDate = date ? new Date(date) : new Date()
  const currYear = currDate.getFullYear()
  const yearStart = new Date(currYear, 0, 1)
  const days = Math.floor(
    (currDate.getTime() - yearStart.getTime()) / (24 * 60 * 60 * 1000)
  )
  return Math.ceil(days / 7)
}

export enum FormattedTimeUnits {
  Minute = 'minute',
  Hour = 'hour',
  Day = 'day',
  Week = 'week',
}

type Config = {
  date?: Date | string | number
  allowedUnits?: FormattedTimeUnits[]
  abbreviateUnit?: boolean
  removeAgo?: boolean
  fallbackText?: string
}

/**
 * *(AI-generated)*
 * Returns a formatted relative time string (e.g. "3 days ago") for a given date, with configurable units and formatting options.
 */
export const formattedTimeSince = ({
  date,
  allowedUnits = [
    FormattedTimeUnits.Minute,
    FormattedTimeUnits.Hour,
    FormattedTimeUnits.Day,
    FormattedTimeUnits.Week,
  ],
  abbreviateUnit = false,
  removeAgo = false,
  fallbackText = 'Data Offline',
}: Config) => {
  if (!date) {
    return fallbackText
  }

  const { weeks, days, hours, minutes } = getTotalDifferenceBetweenDates(
    date,
    new Date()
  )

  let smallestUnit
  if (allowedUnits.includes(FormattedTimeUnits.Minute)) {
    smallestUnit = FormattedTimeUnits.Minute
  } else if (allowedUnits.includes(FormattedTimeUnits.Hour)) {
    smallestUnit = FormattedTimeUnits.Hour
  } else if (allowedUnits.includes(FormattedTimeUnits.Day)) {
    smallestUnit = FormattedTimeUnits.Day
  } else if (allowedUnits.includes(FormattedTimeUnits.Week)) {
    smallestUnit = FormattedTimeUnits.Week
  }

  let unit: FormattedTimeUnits | undefined
  let abbreviation
  let data

  // WEEKS
  if (
    smallestUnit === FormattedTimeUnits.Week ||
    (weeks >= 2 && allowedUnits.includes(FormattedTimeUnits.Week))
  ) {
    unit = FormattedTimeUnits.Week
    abbreviation = 'w'
    data = weeks
  }
  // DAYS
  else if (
    smallestUnit === FormattedTimeUnits.Day ||
    (days >= 1 && allowedUnits.includes(FormattedTimeUnits.Day))
  ) {
    if (days === 0) {
      return 'Today'
    } else if (days === 1) {
      return 'Yesterday'
    }
    unit = FormattedTimeUnits.Day
    abbreviation = 'd'
    data = days
  }
  // HOURS
  else if (
    smallestUnit === FormattedTimeUnits.Hour ||
    (hours >= 1 && allowedUnits.includes(FormattedTimeUnits.Hour))
  ) {
    unit = FormattedTimeUnits.Hour
    abbreviation = 'h'
    data = hours
  }
  // MINUTES
  else if (allowedUnits.includes(FormattedTimeUnits.Minute)) {
    unit = FormattedTimeUnits.Minute
    abbreviation = 'm'
    data = minutes
  }

  if (data === undefined || unit === undefined || abbreviation === undefined) {
    return fallbackText
  }

  const formatter = new Intl.RelativeTimeFormat('en', {
    numeric: 'always',
  })
  let formattedString = formatter.format(data * -1, unit)
  if (abbreviateUnit) {
    const regex = new RegExp(`\\s${unit}s?`, 'g')
    formattedString = formattedString.replace(regex, abbreviation)
  }
  if (removeAgo) {
    formattedString = formattedString.replace(/\s*ago\b/g, '')
  }
  return formattedString
}

/**
 * Create a Date object a number of days ago
 * @param days - number of days ago
 * @returns Javascript Date object x days ago
 */
export const daysAgo = (days: number) => {
  const today = new Date()
  return today.setDate(today.getDate() - days)
}
